import React, { useRef, useState } from 'react';
import PinPad from './PinPad';
import { authFetch, API } from '../api';
import { useScanWedge, ScanEnd } from '../scanWedge';

// The one way a person says who they are (Fernando 19.10): "Identify yourself. Please enter your PIN or scan your
// badge." — a PIN or a badge, never a list of names; the WMS knows who it is from that alone. Used by the P&P station
// now, and meant for every other task screen next (accepting returns, polishing, replenishing, inbounds).

const norm = (s: string) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export const isBadgeCode = (s: string) => /^WB[A-Z2-7]{16}$/.test(norm(s));

export type Identity = { pin?: string; badge?: string };
type How = 'badge' | 'pin';

const DEFAULT_BUSY = (how: How) => (how === 'badge' ? 'Badge read ✓ — one moment…' : 'PIN accepted ✓ — one moment…');

const DEFAULT_HINT = 'Scan your badge or enter your PIN';

export default function IdentifyBox({ title, sub, allowBadge = true, cancelLabel, busyText = DEFAULT_BUSY, hint = DEFAULT_HINT, onSubmit, onCancel }: {
  title: string; sub?: React.ReactNode; allowBadge?: boolean; cancelLabel: string;
  /** Fernando 1.11: "everywhere the PIN screen should also mention that scan a Badge or enter a PIN" — shown under
   *  every title, whatever the caller wrote; '' hides it (a tablet without a scanner) */
  hint?: string;
  /** what the box says while the server works (a round start takes 10+ s with 50 orders — Fernando 1.11: "the PIN
   *  screen does not disappear"); the box shows it with a spinner and ignores further scans until the answer comes */
  busyText?: (how: How) => string;
  onSubmit: (id: Identity) => Promise<string | null>;   // an error to show, or null (the caller moves on)
  onCancel: () => void;
}) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [how, setHow] = useState<How>('pin');
  const busyRef = useRef(false);
  const run = async (id: Identity) => {
    if (busyRef.current) return;                       // a second scan / tap while the first is being answered
    busyRef.current = true;
    setBusy(true); setErr(''); setHow(id.badge ? 'badge' : 'pin');
    try {
      const e = await onSubmit(id);
      if (e) setErr(e);
    } catch { setErr('No connection to the WMS'); } finally { busyRef.current = false; setBusy(false); }
  };
  const runRef = useRef(run);
  runRef.current = run;

  // the badge comes from the fixed scanner (it types like a keyboard, fast; with or without an Enter — 28.10: the
  // station's scanner sends none, and scanWedge reads physical keys so the keyboard layout does not matter); capture
  // phase, so nothing behind this box reacts to the scan
  useScanWedge((code: string, how: ScanEnd) => {
    if (busyRef.current) return;                       // the answer is on its way — this scan changes nothing
    if (isBadgeCode(code)) { runRef.current({ badge: code }); return; }
    const n = norm(code);
    const shape = n.length > 6 ? `${n.slice(0, 2)}…${n.slice(-4)}` : n;
    setErr(`That is not a badge — read ${n.length} characters: ${shape}`);
    // the evidence lands in auth_events (badge_scan_rejected) — never the code itself
    authFetch(`${API}/pp2/badge-reject`, { method: 'POST',
      body: JSON.stringify({ length: n.length, head: n.slice(0, 2), tail: n.slice(-4), how }) }).catch(() => {});
  }, { enabled: allowBadge, capture: true, preventDefault: true });

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-6">
      <div className="max-w-xl w-full max-h-full overflow-y-auto rounded-2xl bg-white p-6 text-center">
        <PinPad title={title} hint={allowBadge ? hint : ''} error={err} busy={busy} cancelLabel={cancelLabel}
          onSubmit={pin => run({ pin })} onCancel={onCancel} />
        {busy && (
          <div className="flex items-center justify-center gap-3 text-[#111111] text-xl font-semibold mt-1" role="status">
            <span className="inline-block w-6 h-6 border-4 border-[#111111] border-t-transparent rounded-full animate-spin" />
            {busyText(how)}
          </div>
        )}
        {sub && <div className="text-[#444444] text-base mt-1">{sub}</div>}
      </div>
    </div>
  );
}
